/**
 * Asambleas — idioma y fechas que ve el propietario (puro, sin Firebase).
 *
 * Las páginas públicas (/asamblea/:t y /z/:t) se muestran en el idioma del
 * propietario que manda el servidor (campo `idioma` de la respuesta: "es",
 * "en" o "pt-BR") y, si no viene, en español. Nunca en el idioma del
 * navegador ni en el que quedó guardado en el dispositivo: el enlace llega por
 * WhatsApp y se abre en cualquier teléfono.
 *
 * Las fechas se arman a mano con las partes de Intl (hora de Montevideo, reloj
 * de 24 horas), así salen iguales en cualquier navegador:
 *   es     "miércoles 21 de octubre, 10:10"
 *   en     "Wednesday, October 21, 10:10"
 *   pt-BR  "quarta-feira, 21 de outubro, 10:10"
 */

export const IDIOMAS_ASAMBLEA = Object.freeze(["es", "en", "pt-BR"]);
export const IDIOMA_POR_DEFECTO = "es";

const TZ = "America/Montevideo";
const ETIQUETA_INTL = { es: "es-UY", en: "en-US", "pt-BR": "pt-BR" };

/** "es" | "en" | "pt-BR" si el valor es un idioma de la app ("pt", "pt_BR", "en-US"…), o null. */
export function idiomaSoportado(valor) {
  if (typeof valor !== "string") return null;
  const v = valor.trim();
  if (IDIOMAS_ASAMBLEA.includes(v)) return v;
  const base = v.toLowerCase().replace(/_/g, "-").split("-")[0];
  if (base === "es") return "es";
  if (base === "en") return "en";
  if (base === "pt") return "pt-BR";
  return null;
}

/** El idioma soportado o, si no lo es (o no vino), español. */
export const normalizarIdioma = (valor) => idiomaSoportado(valor) || IDIOMA_POR_DEFECTO;

const formatos = new Map();

/** Partes (weekday, day, month, hour, minute…) de un instante en Montevideo. */
function partes(etiqueta, opciones, ms) {
  const clave = `${etiqueta}|${JSON.stringify(opciones)}`;
  if (!formatos.has(clave)) formatos.set(clave, new Intl.DateTimeFormat(etiqueta, { timeZone: TZ, ...opciones }));
  const out = {};
  formatos
    .get(clave)
    .formatToParts(new Date(ms))
    .forEach((p) => {
      // Sin espacios raros (U+202F, U+00A0) dentro de una parte.
      if (p.type !== "literal") out[p.type] = String(p.value).replace(/\s+/g, " ").trim();
    });
  return out;
}

const msDe = (ms) => {
  const n = Number(ms);
  return ms != null && ms !== "" && Number.isFinite(n) ? n : null;
};

/** "miércoles 21 de octubre" · "Wednesday, October 21" · "quarta-feira, 21 de outubro" ("" sin fecha). */
export function fechaLargaAsamblea(ms, idioma) {
  const n = msDe(ms);
  if (n == null) return "";
  const lengua = normalizarIdioma(idioma);
  const p = partes(ETIQUETA_INTL[lengua], { weekday: "long", day: "numeric", month: "long" }, n);
  if (lengua === "en") return `${p.weekday}, ${p.month} ${p.day}`;
  if (lengua === "pt-BR") return `${p.weekday}, ${p.day} de ${p.month}`;
  return `${p.weekday} ${p.day} de ${p.month}`;
}

/** "10:10" (24 horas, Montevideo). en-GB ya es de 24 horas aunque el motor ignore hourCycle. */
export function horaAsamblea(ms) {
  const n = msDe(ms);
  if (n == null) return "";
  const p = partes("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }, n);
  return `${p.hour}:${p.minute}`;
}

/** "miércoles 21 de octubre, 10:10" (o en el idioma pedido; "" sin fecha). */
export function fechaHoraAsamblea(ms, idioma) {
  const fecha = fechaLargaAsamblea(ms, idioma);
  return fecha ? `${fecha}, ${horaAsamblea(ms)}` : "";
}

/** "26 oct 2026" · "Oct 26, 2026" · "26 out 2026" (Montevideo; "" sin fecha). */
export function fechaCortaAsamblea(ms, idioma) {
  const n = msDe(ms);
  if (n == null) return "";
  const lengua = normalizarIdioma(idioma);
  const p = partes(ETIQUETA_INTL[lengua], { day: "numeric", month: "short", year: "numeric" }, n);
  // Sin el punto de la abreviatura ("oct.", "out."): en una fila se lee más limpio.
  const mes = String(p.month || "").replace(/\.$/, "");
  if (lengua === "en") return `${mes} ${p.day}, ${p.year}`;
  return `${p.day} ${mes} ${p.year}`;
}

/** "YYYY-MM-DD" (fecha calendario) → mediodía de Montevideo en ms, o null: sin corrimiento de día. */
export function msDeFechaIso(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 15) : null;
}

/** 33.34 → "33,3" (es, pt-BR) · "33.3" (en): hasta un decimal ("" si no es un número). */
export function porcentajeAsamblea(valor, idioma) {
  const n = Number(valor);
  if (valor == null || valor === "" || !Number.isFinite(n)) return "";
  return new Intl.NumberFormat(ETIQUETA_INTL[normalizarIdioma(idioma)], { maximumFractionDigits: 1 }).format(n);
}
